import React, { useContext, useMemo, useEffect } from 'react';
import _ from 'underscore';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { ValidationStringMap } from '@bingads-webui/validation-string-map';
import { Collapse, TextTitle, useCollapseState, ValidatedFixedPanel, ValidatedPanel } from '@bingads-webui-campaign/wizard-jumbo-card';
import { Campaign } from '@bingads-webui/campaign-odata-schemas';
import { ValidationGroup } from '@bingads-webui-react/validation';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import {
  FormErrorGroup, entityIs, andComp, propertyIs,
} from '@bingads-webui-campaign-react/form-error';
import { Stack } from '@fluentui/react';
import { ServerContext } from '@bingads-webui-campaign-react/server-context';
import { useWizardService, matchStepState, STEP_STATES } from '@bingads-webui-react/wizard';
import { ActivityContext } from '@bingads-webui-react/hoc-with-signal';
import { getBidBudgetTitleDetails, ValidatedBidValue } from '@bingads-webui-campaign-react/wizard-shared-components';
import { ValidatedLegacyBudget } from '@bingads-webui-campaign-react/step-wizard-controls';
import { AttentionBeaconGroup } from '@bingads-webui-campaign-react/attention-beacon';
import { ActuationGroup } from '@bingads-webui-campaign-react/actuator';
import { RemainingCharsValidatedTextField } from '@bingads-webui-campaign-react/remaining-chars-hint';
import { useFormService } from '@bingads-webui-react/form';
import { withUnvalidatedControlErrors, withControlErrorHandling } from '@bingads-webui-campaign-react/error-handling';

import { StepHeader } from '../../components/step-header';
import { SectionShimmer } from '../../components/section-shimmer';
import BudgetBidImg from './media/budget-bid.svg';
import { ERROR_ENTITY } from '../../model/api-errors';
import { getCampaignStyles } from './styles';
import { useExitWithoutSavingConfirmation } from '../../components/use-exit-without-saving-confirmation';

const entityIsCampaign = entityIs(ERROR_ENTITY.Campaign);
const campaignBudgetProp = andComp(entityIsCampaign, propertyIs('Budget'));
const campaignNameProp = andComp(entityIsCampaign, propertyIs('Name'));
const ServerValidatedLegacyBudget = withControlErrorHandling(withUnvalidatedControlErrors(ValidatedLegacyBudget, { filter: campaignBudgetProp }));

const getPanelErrorProps = ({ needsAttention }) => (needsAttention ? {
  error: _TL_("* Something's wrong"),
} : {});

export const CampaignSettingStepShell = ({
  data,
  onDataChange,
  error,
}) => {
  const i18n = useContext(I18nContext);

  const classes = useDefaultStyles(getCampaignStyles);

  const serverContext = useContext(ServerContext);

  const { state } = useWizardService();

  const activity = useContext(ActivityContext);

  const currentStates = state.toStrings()[1];

  const { campaign } = data || {};

  const { Id: campaignId } = campaign || {};

  const legacyI18n = useMemo(() => buildLegacyI18n(i18n, [ValidationStringMap]), [i18n]);

  const { state: { context: { saveFailed: showAllValidationErrors } } } = useFormService();

  const [navigationConfirmationPrompt] = useExitWithoutSavingConfirmation(i18n);

  const onValidate = (result) => {
    if (data && data.campaign) {
      onDataChange({
        data: {
          ...data,
          campaign: {
            ...data.campaign,
            ...result.data,
            BiddingScheme: {
              ...campaign.BiddingScheme,
              ..._.pick(result.data, 'ManualCPI'),
            },
          },
        },
        isValid: result.isValid,
      });
    }
  };

  const [
    allCampaignSettingsSections,
    campaignSettingsExpandedSections,
    setCampaignSettingsExpandedSections,
  ] = useCollapseState(
    ['campaign-name', 'budget-bid'] /* all sections */,
    ['campaign-name', 'budget-bid'] /* initially expanded sections */
  );

  const bidBudgetTitleDetails = useMemo(
    () => (campaign ? getBidBudgetTitleDetails(serverContext, i18n, campaign.BiddingScheme, campaign.Budget) : null),
    [campaign, i18n, serverContext]
  );

  useEffect(() => {
    if (activity && matchStepState(state, STEP_STATES.LOADING)) {
      activity.signal(`onCampaignStepLoading - ${currentStates}`);
    }

    if (activity && matchStepState(state, STEP_STATES.SAVING)) {
      activity.signal(`onCampaignStepSaving - ${currentStates}`);
    }
  }, [activity, currentStates, state]);

  return (
    <div className={classNames(classes.campaignShell, 'campaign-shell', 'campaign-setting-step')}>
      <ActuationGroup actuationProps={{ isActive: false }}>
        <AttentionBeaconGroup>
          <FormErrorGroup formErrorProps={{ errors: error || [], showAllValidationErrors }}>
            <ValidationGroup
              validationProps={{
                schema: Campaign,
                onValidate,
                data: (data && data.campaign) || {},
                i18n: legacyI18n,
            }}
            >
              <div className={classNames('campaign-step', classes.stepContainer)}>
                <div>
                  <Stack token={{ childrenGap: 14 }}>
                    <StepHeader
                      title={_TL_('Campaign settings')}
                      description={_TL_('Tell us about your campaign.')}
                    />
                  </Stack>
                </div>
                <Stack horizontal token={{ childrenGap: 14 }}>
                  <Collapse allPanelItems={allCampaignSettingsSections} checkItems skipItemsTypeCheck activeKeys={campaignSettingsExpandedSections} setActiveKeys={setCampaignSettingsExpandedSections}>
                    <ValidatedFixedPanel
                      key="campaign-name"
                      title={<TextTitle titleKey={_TL_('Campaign name')} topicKey="pop_BA_CampaignCreate_Settings_CampaignName" />}
                      attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                    >
                      <RemainingCharsValidatedTextField
                        placeholder={_TL_('Enter a name for your campaign')}
                        id="input-campaign-name"
                        validationProps={{
                          field: 'Name',
                          schema: Campaign,
                        }}
                        i18n={i18n}
                        formErrorProps={{ filter: campaignNameProp }}
                      />
                    </ValidatedFixedPanel>
                    <ValidatedPanel
                      key="budget-bid"
                      title={<TextTitle
                        titleKey={_TL_('Budget & bid strategy')}
                        details={bidBudgetTitleDetails}
                        icon={BudgetBidImg}
                      />}
                      attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
                    >
                      {
                        data && data.campaign ? (
                          <div>
                            <ServerValidatedLegacyBudget
                              className="vanila-budget"
                              campaignId={campaignId}
                              validationProps={{
                                data: { Budget: campaign.Budget },
                              }}
                            />
                            <div className={classNames(classes.bidStrategy)}>
                              <TextTitle
                                titleKey={_TL_('Bid strategy')}
                                topicKey="pop_BA_StoreAds_BidStrategy"
                              />
                              <div>
                                {i18n.getString(_TL_('Manual CPI'))}
                              </div>
                            </div>
                            <ValidatedBidValue
                              validationProps={{
                                data: campaign,
                                bidValueType: 'ManualCPI',
                              }}
                            />
                          </div>
                        ) : (<SectionShimmer count={3} />)
                      }
                    </ValidatedPanel>
                  </Collapse>
                </Stack>
              </div>
            </ValidationGroup>
            {navigationConfirmationPrompt}
          </FormErrorGroup>
        </AttentionBeaconGroup>
      </ActuationGroup>
    </div>
  );
};

CampaignSettingStepShell.propTypes = {
  data: PropTypes.shape({
    campaign: PropTypes.shape({}).isRequired,
  }).isRequired,
  onDataChange: PropTypes.func.isRequired,
  error: PropTypes.shape({}).isRequired,
};

